import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box, Paper, Typography, Button, Divider,
  Tabs, Tab, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Chip, Snackbar,
  LinearProgress, Alert, IconButton,
  CircularProgress, Stack, Card, CardContent
} from '@mui/material';
import {
  Close as CloseIcon,
  Assessment as AssessmentIcon,
  BugReport as BugReportIcon,
  Book as BookIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import API_CONFIG from '../config/api';
import ImpactAnalysisModal from './ImpactAnalysis/ImpactAnalysisModal';
import GenerateTestCasesModal from './ImpactAnalysis/GenerateTestCasesModal';
import RequirementAnalysisModal from './RequirementAnalysis/RequirementAnalysisModal';
import ProductKnowledgeAdd from './ProductKnowledgeAdd';
import ProductKnowledgeDocumentModal from './ProductKnowledge/ProductKnowledgeDocumentModal';
import ProductKnowledgeFilters from './ProductKnowledge/ProductKnowledgeFilters';
import ProductKnowledgeTable from './ProductKnowledge/ProductKnowledgeTable';
import { getJiraInfo } from './ProductKnowledge/productKnowledgeUtils';
import useProductKnowledgeData from './ProductKnowledge/hooks/useProductKnowledgeData';
import useProductKnowledgeDocument from './ProductKnowledge/hooks/useProductKnowledgeDocument';
import useProductKnowledgeReimport from './ProductKnowledge/hooks/useProductKnowledgeReimport';
import useProductKnowledgeTableState from './ProductKnowledge/hooks/useProductKnowledgeTableState';
import useProductKnowledgeRequirementAnalysis from './ProductKnowledge/hooks/useProductKnowledgeRequirementAnalysis';
import { BUG_RCA_COPY, resolveGapTypeLabel, resolveHistoricalScopeNote, formatRcaAnalyzedCaption, hasValidRcaPayload } from './BugRca/bugRcaUiConfig';
import useProductKnowledgeBugRca from './ProductKnowledge/hooks/useProductKnowledgeBugRca';
import { SV } from '../automation/stratovaiTestIds';

const ProductKnowledge = ({ selectedProject }) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [confirmMoveToStagedOpen, setConfirmMoveToStagedOpen] = useState(false);
  const [pendingMoveToStagedIds, setPendingMoveToStagedIds] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
    persist: false,
  });
  const [generationNotice, setGenerationNotice] = useState(null);
  const [generatingTestCases, setGeneratingTestCases] = useState({});
  
  // Impacted Areas state
  const [impactedAreasOpen, setImpactedAreasOpen] = useState(false);
  const [impactedAreasData, setImpactedAreasData] = useState(null);
  const [currentAnalyzedItem, setCurrentAnalyzedItem] = useState(null);
  const [impactAnalysisLoading, setImpactAnalysisLoading] = useState(false);
  const [impactAnalysisProgress, setImpactAnalysisProgress] = useState(0);
  const [jiraBaseUrl, setJiraBaseUrl] = useState(null);
  const impactAnalysisRequestRef = useRef(0);
  
  // Test case generation modal state
  const [testCaseGenModalOpen, setTestCaseGenModalOpen] = useState(false);
  const [testCaseGenItem, setTestCaseGenItem] = useState(null);

  const {
    staged,
    approved,
    deleted,
    generatedItems,
    setGeneratedItems,
    loadApproved,
    loadGeneratedState,
    refreshPrimaryLists,
    refreshCurrentTab,
    handleUploadFile,
    handlePasteText,
    handleJiraImport,
    handleBulkAction,
    handleMoveToStaged,
  } = useProductKnowledgeData({ selectedProject, setSnackbar });

  const {
    rcaModalOpen,
    rcaData,
    rcaLoading,
    rcaGenerating,
    rcaIssueKey,
    handleOpenRca,
    handleGenerateRca,
    handleRefreshRca,
    closeRcaModal,
  } = useProductKnowledgeBugRca({ setSnackbar, refreshPrimaryLists });
  
  const {
    selectedIds,
    setSelectedIds,
    activeTab,
    setActiveTab,
    expandedParents,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    sortConfig,
    searchTerm,
    setSearchTerm,
    filters,
    setFilters,
    hierarchyView,
    setHierarchyView,
    showFilters,
    setShowFilters,
    isSubtaskItem,
    subtasksByParentKey,
    filterOptions,
    baseFilteredItems,
    sortedItems,
    totalPages,
    startIndex,
    paginatedItems,
    stagedTabCount,
    approvedTabCount,
    deletedTabCount,
    handleSort,
    handleSelectAll,
    handleSelectItem,
    handleDeleteSelected,
    handleToggleParentExpanded,
    emptyStateMessage,
  } = useProductKnowledgeTableState({ staged, approved, deleted });

  const fetchJiraBaseUrl = useCallback(async () => {
    try {
      if (!selectedProject?.project_id) {
        return;
      }
      
      const token = localStorage.getItem('stratovai_access_token');
      const projectId = selectedProject.project_id || selectedProject.id;
      const res = await fetch(`${API_CONFIG.BASE_URL}/api/config?project_id=${encodeURIComponent(projectId)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const json = await res.json();
        const baseUrl = json.config?.['project.jira_base_url'];
        if (baseUrl) {
          setJiraBaseUrl(baseUrl);
        }
      }
    } catch (e) {
      // Silently fail - JIRA links just won't work
      console.warn('Could not fetch JIRA base URL:', e);
    }
  }, [selectedProject]);

  const {
    docDialogOpen,
    openDocument,
    closeDocument,
    docContent,
    setDocContent,
    imageBlobUrls,
  } = useProductKnowledgeDocument({
    selectedProject,
    staged,
    approved,
    setSnackbar,
  });

  const {
    reimportLoadingModal,
    reimportProgress,
    reimportMessage,
    reimportDetailLine,
    closeReimportLoadingModal,
    figmaReimportDialogOpen,
    figmaReimportPlan,
    closeFigmaReimportDialog,
    runFigmaReimportExecute,
  } = useProductKnowledgeReimport({
    selectedProject,
    setSnackbar,
    refreshPrimaryLists,
  });

  const {
    requirementAnalysisLoading,
    onAnalyzeRequirement,
    requirementAnalysisModalProps,
  } = useProductKnowledgeRequirementAnalysis({
    projectId: selectedProject?.project_id,
    setSnackbar,
  });

  useEffect(() => {
    refreshCurrentTab(activeTab);
    if (activeTab !== 'approved') {
      loadApproved();
    }
    loadGeneratedState();
    if (selectedProject?.project_id) {
      fetchJiraBaseUrl();
    }
  }, [activeTab, fetchJiraBaseUrl, loadApproved, loadGeneratedState, refreshCurrentTab, selectedProject?.project_id]);

  const getJiraKeyForItem = useCallback((item) => {
    return getJiraInfo(item).jiraKey;
  }, []);

  const handleGenerateTestCases = async (item, isRegenerate = false) => {
    const jiraKey = getJiraInfo(item).jiraKey;
    
    if (!jiraKey) {
      // Fallback to old endpoint for non-JIRA entries
      if (!selectedProject?.project_id) return;
      
      try {
        setGeneratingTestCases(prev => ({ ...prev, [item.knowledge_id]: true }));
        
        const res = await API_CONFIG.request(`/pkb/${selectedProject.project_id}/generate-test-cases`, {
          method: 'POST',
          body: JSON.stringify({ 
            knowledge_id: item.knowledge_id,
            regenerate: isRegenerate
          }),
        });
        
        const json = await res.json();
        if (json?.success) {
          setSnackbar({ 
            open: true, 
            message: `Generated ${json.test_cases_count || 0} comprehensive test cases.`, 
            severity: 'success' 
          });
          setGenerationNotice({
            count: json.test_cases_count || 0,
            generationRunId: json.generation_run_id || null,
            sourceKnowledgeId: item.knowledge_id,
          });
          setGeneratedItems(prev => new Set([...prev, item.knowledge_id]));
          loadGeneratedState();
        } else {
          throw new Error(json?.detail || 'Test case generation failed');
        }
      } catch (e) {
        setSnackbar({ open: true, message: e.message || 'Test case generation failed', severity: 'error' });
      } finally {
        setGeneratingTestCases(prev => ({ ...prev, [item.knowledge_id]: false }));
      }
      return;
    }
    
    // For JIRA entries, open the new modal immediately
    setTestCaseGenItem(item);
    setTestCaseGenModalOpen(true);
    // Don't set generating state - let the modal handle it
  };

  const handleTestCasesSaved = (savedCount) => {
    const count = typeof savedCount === 'number' ? savedCount : (savedCount?.savedCount || 0);
    const generationRunId = typeof savedCount === 'object' ? (savedCount?.generationRunId || null) : null;
    setSnackbar({ 
      open: true, 
      message: `Saved ${count} test cases successfully!`, 
      severity: 'success' 
    });
    setGenerationNotice({
      count,
      generationRunId,
      sourceKnowledgeId: testCaseGenItem?.knowledge_id || null,
    });
    loadGeneratedState();
    setTestCaseGenModalOpen(false);
    setTestCaseGenItem(null);
  };

  const navigateToTestCases = (latestRunOnly = false) => {
    if (latestRunOnly && generationNotice?.generationRunId) {
      sessionStorage.setItem('test_cases_latest_run_id', generationNotice.generationRunId);
    } else {
      sessionStorage.removeItem('test_cases_latest_run_id');
    }
    window.dispatchEvent(new CustomEvent('navigateToTab', { detail: { tab: 'testCases' } }));
    setGenerationNotice(null);
  };

  const handleRegenerateClick = (item) => {
    handleGenerateTestCases(item, true);
  };

  const appendImpactRequestRef = (text, rid) => {
    if (!rid) return text;
    return `${text}\n\nReference: ${rid}`;
  };

  const IMPACT_ANALYSIS_TIMEOUT_USER_MSG =
    'Impact analysis is taking longer than expected. Please retry in a few seconds. If processing completes in the background, the next run may return the result.';

  const handleViewImpactedAreas = async (item) => {
    if (!selectedProject?.project_id) return;
    const requestId = impactAnalysisRequestRef.current + 1;
    impactAnalysisRequestRef.current = requestId;
    
    // Funny messages for impact analysis
    const funnyMessages = [
      "🧠 Analyzing semantic connections...",
      "🔍 Searching for hidden dependencies...",
      "⚡ Calculating impact probabilities...",
      "🎯 Identifying potential conflicts...",
      "🤖 AI is thinking really hard...",
      "📊 Crunching similarity numbers...",
      "🔮 Predicting the future of your code...",
      "🎪 Juggling multiple analysis algorithms...",
      "🚀 Launching impact detection satellites...",
      "🧮 Doing complex math that would make Einstein proud...",
      "🎭 Putting on our detective hats...",
      "🍕 Fueling up on digital coffee...",
      "🎨 Painting a picture of your dependencies...",
      "🎵 Harmonizing your project components...",
      "🏃‍♂️ Running analysis marathons..."
    ];
    
    try {
      setImpactAnalysisLoading(true);
      setImpactAnalysisProgress(0);
      setImpactedAreasData(null);
      setCurrentAnalyzedItem(item);
      setImpactedAreasOpen(false);
      
      // Simulate progress with funny messages
      let progressInterval;
      let messageIndex = 0;
      
      const updateProgress = () => {
        setImpactAnalysisProgress(prev => {
          if (prev >= 90) return prev; // Stop at 90% until real completion
          return prev + Math.random() * 15;
        });
        
        setSnackbar({
          open: true,
          message: funnyMessages[messageIndex % funnyMessages.length],
          severity: 'info',
          persist: false,
        });
        
        messageIndex++;
      };
      
      // Start progress updates
      progressInterval = setInterval(updateProgress, 800);
      
      let res;
      try {
        res = await API_CONFIG.request(`/pkb/${selectedProject.project_id}/analyze-impacted-areas`, {
          method: 'POST',
          body: JSON.stringify({ 
            knowledge_id: item.knowledge_id,
            prd_content: item.content
          }),
        });
      } finally {
        // Always clear interval regardless of success/failure
        clearInterval(progressInterval);
      }
      
      // Complete progress
      setImpactAnalysisProgress(100);
      
      let json;
      try {
        json = await res.json();
      } catch (parseErr) {
        if (impactAnalysisRequestRef.current !== requestId) return;
        setSnackbar({
          open: true,
          message: 'Impact analysis returned an invalid response. Please try again.',
          severity: 'error',
          persist: true,
        });
        return;
      }

      if (impactAnalysisRequestRef.current !== requestId) {
        return;
      }

      if (!res.ok) {
        const rid = json?.request_id;
        let msg;
        let severity = 'error';
        if (res.status === 504 || json?.status === 'timeout') {
          severity = 'warning';
          msg = appendImpactRequestRef(IMPACT_ANALYSIS_TIMEOUT_USER_MSG, rid);
        } else if (res.status === 429 || json?.status === 'busy') {
          severity = 'warning';
          msg = appendImpactRequestRef(
            json?.message ||
              'Impact analysis is currently busy. Please try again in a few seconds.',
            rid,
          );
        } else {
          const base =
            json?.message ||
            json?.detail ||
            `Impact analysis failed (${res.status}).`;
          msg = appendImpactRequestRef(base, rid);
        }
        setImpactedAreasOpen(false);
        setSnackbar({ open: true, message: msg, severity, persist: true });
        return;
      }

      if (json?.status === 'timeout' || json?.status === 'error' || json?.status === 'busy') {
        const rid = json?.request_id;
        let msg;
        let severity = 'error';
        if (json?.status === 'timeout') {
          severity = 'warning';
          msg = appendImpactRequestRef(IMPACT_ANALYSIS_TIMEOUT_USER_MSG, rid);
        } else if (json?.status === 'busy') {
          severity = 'warning';
          msg = appendImpactRequestRef(
            json?.message ||
              'Impact analysis is currently busy. Please try again in a few seconds.',
            rid,
          );
        } else {
          msg = appendImpactRequestRef(json?.message || 'Impact analysis failed.', rid);
        }
        setImpactedAreasOpen(false);
        setSnackbar({ open: true, message: msg, severity, persist: true });
        return;
      }

      if (json?.success) {
        // Open impacted areas modal/dialog
        const combined = Array.isArray(json.direct_links) ? [...json.direct_links] : [];
        if (Array.isArray(json.ai_suggested)) combined.push(...json.ai_suggested);
        setImpactedAreasData({
          impacts: combined,
          impactedAreas: Array.isArray(json.impacted_areas) ? json.impacted_areas : [],
          impactSummary: json.impact_summary || null,
          testFocus: json.test_focus || null,
          supportingProductKnowledge: Array.isArray(json.supporting_product_knowledge)
            ? json.supporting_product_knowledge
            : null,
          moduleRollup: json.module_rollup && typeof json.module_rollup === 'object' ? json.module_rollup : null,
          moduleImpacts: json.module_impacts && typeof json.module_impacts === 'object' ? json.module_impacts : null,
          diagnostics: json.diagnostics && typeof json.diagnostics === 'object' ? json.diagnostics : null,
        });
        setCurrentAnalyzedItem(item);
        setImpactedAreasOpen(true);
        setSnackbar({
          open: true,
          message: json.message || `Found ${json.total_impacts ?? (combined?.length || 0)} impacted areas in your PKB and test cases.`,
          severity: 'success',
          persist: false,
        });
      } else {
        const rid = json?.request_id;
        setSnackbar({
          open: true,
          message: appendImpactRequestRef(
            json?.message || json?.detail || 'Impacted areas analysis failed',
            rid,
          ),
          severity: 'error',
          persist: true,
        });
      }
    } catch (e) {
      if (impactAnalysisRequestRef.current === requestId) {
        setSnackbar({
          open: true,
          message: e.message || 'Impacted areas analysis failed',
          severity: 'error',
          persist: true,
        });
      }
    } finally {
      if (impactAnalysisRequestRef.current === requestId) {
        setImpactAnalysisLoading(false);
        setImpactAnalysisProgress(0);
      }
    }
  };

  return (
    <Box data-testid={SV.pk.page} sx={{ p: 2, pt: 1 }}>
      {/* Header bar */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 2,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Box>
          <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 600 }}>Product Knowledge</Typography>
          <Typography variant="body2" color="text.secondary">
            Ingest, organize & curate Product Knowledge
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ProductKnowledgeAdd 
            selectedProject={selectedProject}
            onUploadFile={handleUploadFile}
            onPasteText={handlePasteText}
            onJiraImport={handleJiraImport}
            onFigmaIngestCompleted={async (result) => {
              try {
                await refreshPrimaryLists();
                setSnackbar({
                  open: true,
                  message: 'Figma knowledge updated (staged).',
                  severity: 'success'
                });
              } catch (e) {
                setSnackbar({ open: true, message: e.message || 'Figma ingestion completed, but refresh failed.', severity: 'warning' });
              }
            }}
          />
        </Box>
      </Box>

      <Paper sx={{ p: 2 }}>
        {generationNotice && (
          <Alert
            severity="success"
            sx={{ mb: 2 }}
            action={
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button color="inherit" size="small" onClick={() => navigateToTestCases(false)}>
                  View test cases
                </Button>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigateToTestCases(true)}
                  disabled={!generationNotice.generationRunId}
                >
                  View latest run
                </Button>
                <Button color="inherit" size="small" onClick={() => setGenerationNotice(null)}>
                  Dismiss
                </Button>
              </Box>
            }
          >
            {generationNotice.count} test case{generationNotice.count === 1 ? '' : 's'} generated.
          </Alert>
        )}
        {/* Tabs Display */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Tabs 
            data-testid={SV.pk.tabs}
            value={activeTab} 
            onChange={(_, v) => { setSelectedIds([]); setActiveTab(v); }}
            sx={{
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
              },
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '0.95rem',
                minHeight: 48,
                px: 3,
                '&.Mui-selected': {
                  color: 'primary.main',
                },
              },
            }}
          >
            <Tab 
              value="staged"
              data-testid={SV.pk.tabStaged}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Staged
                  <Chip 
                    label={stagedTabCount} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.75rem',
                      backgroundColor: 'warning.light',
                      color: 'warning.contrastText',
                      fontWeight: 600
                    }} 
                  />
                </Box>
              } 
            />
            <Tab 
              value="approved"
              data-testid={SV.pk.tabApproved}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Approved
                  <Chip 
                    label={approvedTabCount} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.75rem',
                      backgroundColor: 'success.light',
                      color: 'success.contrastText',
                      fontWeight: 600
                    }} 
                  />
                </Box>
              } 
            />
            <Tab 
              value="deleted"
              data-testid={SV.pk.tabDeleted}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  Deleted
                  <Chip 
                    label={deletedTabCount} 
                    size="small" 
                    sx={{ 
                      height: 20, 
                      fontSize: '0.75rem',
                      backgroundColor: 'error.light',
                      color: 'error.contrastText',
                      fontWeight: 600
                    }} 
                  />
                </Box>
              } 
            />
          </Tabs>
        </Box>

        <ProductKnowledgeFilters
          activeTab={activeTab}
          selectedCount={selectedIds.length}
          onRefresh={() => refreshCurrentTab(activeTab)}
          onDeleteSelected={() => handleDeleteSelected((ids) => {
            setPendingDeleteIds(ids);
            setConfirmDeleteOpen(true);
          })}
          onMoveToStagedSelected={() => {
            setPendingMoveToStagedIds(selectedIds);
            setConfirmMoveToStagedOpen(true);
          }}
          onApproveSelected={() => handleBulkAction('promote', selectedIds, () => setSelectedIds([]))}
          onRestoreSelected={() => handleBulkAction('restore', selectedIds, () => setSelectedIds([]))}
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          onClearSearch={() => setSearchTerm('')}
          showFilters={showFilters}
          onToggleFilters={() => setShowFilters((prev) => !prev)}
          filters={filters}
          onFiltersChange={setFilters}
          hierarchyView={hierarchyView}
          onHierarchyViewChange={setHierarchyView}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
          filterOptions={filterOptions}
        />

        <Divider sx={{ mb: 2 }} />

        {/* Results Summary */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {sortedItems.length > 0 ? (
              <>
                Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, sortedItems.length)} of {sortedItems.length} items
                {searchTerm && ` matching "${searchTerm}"`}
                {hierarchyView === 'top_level' && (() => {
                  const hidden = baseFilteredItems.filter(i => isSubtaskItem(i)).length;
                  return hidden > 0 ? ` (+${hidden} subtasks hidden)` : '';
                })()}
              </>
            ) : (
              <>No items to display{searchTerm && ` matching "${searchTerm}"`}</>
            )}
          </Typography>
        </Box>

        <ProductKnowledgeTable
          activeTab={activeTab}
          sortedItems={sortedItems}
          emptyStateMessage={emptyStateMessage}
          paginatedItems={paginatedItems}
          selectedIds={selectedIds}
          sortConfig={sortConfig}
          onSort={handleSort}
          onSelectAll={handleSelectAll}
          onSelectItem={handleSelectItem}
          onPreviewItem={openDocument}
          hierarchyView={hierarchyView}
          isSubtaskItem={isSubtaskItem}
          subtasksByParentKey={subtasksByParentKey}
          expandedParents={expandedParents}
          onToggleParentExpanded={handleToggleParentExpanded}
          generatedItems={generatedItems}
          generatingTestCases={generatingTestCases}
          rcaGenerating={rcaGenerating}
          getJiraKeyForItem={getJiraKeyForItem}
          onOpenRca={handleOpenRca}
          onGenerateItem={handleGenerateTestCases}
          onRegenerateItem={handleRegenerateClick}
          onViewImpactedAreas={handleViewImpactedAreas}
          onAnalyzeRequirement={onAnalyzeRequirement}
          onGenerateRca={handleGenerateRca}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </Paper>

      <ProductKnowledgeDocumentModal
        open={docDialogOpen}
        onClose={closeDocument}
        docContent={docContent}
        imageBlobUrls={imageBlobUrls}
        projectId={selectedProject?.project_id}
        onFramesUpdated={(newFrames) => {
          setDocContent((prev) => ({ ...prev, figmaFrames: newFrames, hasFigma: newFrames.length > 0 }));
        }}
      />

      {/* Impact Analysis Progress Dialog */}
      <Dialog
        open={impactAnalysisLoading}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 'data-testid': SV.pk.impactProgressDialog }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            Analyzing Impacted Areas
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>
              {snackbar.message}
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={impactAnalysisProgress} 
              sx={{ 
                height: 8, 
                borderRadius: 4,
                mb: 1,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 4,
                }
              }} 
            />
            <Typography variant="body2" color="text.secondary">
              {impactAnalysisProgress >= 95
                ? 'Finalizing analysis...'
                : `${Math.round(impactAnalysisProgress)}% complete`}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications — persistent until dismissed when persist is set (impact analysis errors) */}
      <Snackbar
        open={snackbar.open && !impactAnalysisLoading && !requirementAnalysisLoading}
        autoHideDuration={snackbar.persist ? null : 4000}
        onClose={(_e, reason) => {
          if (snackbar.persist && reason === 'clickaway') return;
          setSnackbar((s) => ({ ...s, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity || 'info'}
          variant="filled"
          sx={{ width: '100%', maxWidth: 560, whiteSpace: 'pre-wrap' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to move {pendingDeleteIds.length} selected entr{pendingDeleteIds.length === 1 ? 'y' : 'ies'} to Deleted? You can restore from the Deleted tab within retention period.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => {
            handleBulkAction('delete', pendingDeleteIds, () => setSelectedIds([]));
            setConfirmDeleteOpen(false);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmMoveToStagedOpen} onClose={() => setConfirmMoveToStagedOpen(false)}>
        <DialogTitle>Move selected items to Staged?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will move {pendingMoveToStagedIds.length} approved knowledge item{pendingMoveToStagedIds.length === 1 ? '' : 's'} to the Staged section.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmMoveToStagedOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => {
            handleMoveToStaged(pendingMoveToStagedIds, () => setSelectedIds([]));
            setConfirmMoveToStagedOpen(false);
          }}>Move to Staged</Button>
        </DialogActions>
      </Dialog>

      <RequirementAnalysisModal {...requirementAnalysisModalProps} />

      {/* Impacted Areas Modal - New Improved Version */}
      <ImpactAnalysisModal
        open={impactedAreasOpen}
        onClose={() => setImpactedAreasOpen(false)}
        impactData={impactedAreasData}
        sourceItem={currentAnalyzedItem}
        projectId={selectedProject?.project_id}
        jiraBaseUrl={jiraBaseUrl}
        onViewPKB={(impact) => {
          // Test-case impacts have a test_case_id but no Jira key — nothing to navigate to in PKB
          if (impact.type === 'test_case') {
            setSnackbar({ open: true, message: 'Navigate to Test Cases to view this test case', severity: 'info' });
            return;
          }
          // PKB impacts: find by jira_issue_key or title
          const targetKey = impact.jira_issue_key;
          const allItems = [...staged, ...approved];
          const targetItem = allItems.find(item => {
            const content = item.content || {};
            const metadata = content.metadata || {};
            const itemKey = content.external_key || 
                          (metadata.id && typeof metadata.id === 'string' && metadata.id.match(/JIRA:(.+)/)?.[1]) ||
                          content.jira_issue_key;
            if (targetKey && itemKey === targetKey) return true;
            // Fallback: match by title if no jira key
            if (!targetKey) {
              const itemTitle = content.name || content.title || item.title || '';
              return itemTitle === impact.title;
            }
            return false;
          });
          if (targetItem) {
            openDocument(targetItem);
            setImpactedAreasOpen(false);
          } else {
            setSnackbar({ open: true, message: 'PKB entry not found', severity: 'warning' });
          }
        }}
        onFeedback={(knowledgeId, targetTitle, label) => {
          API_CONFIG.request(`/pkb/${selectedProject.project_id}/impact-feedback`, {
            method: 'POST',
            body: JSON.stringify({ knowledge_id: knowledgeId, target_title: targetTitle, label })
          }).then((res) => {
            if (res && res.ok) {
              setSnackbar({ open: true, message: 'Feedback submitted', severity: 'success' });
            } else {
              setSnackbar({ open: true, message: 'Failed to submit feedback', severity: 'error' });
            }
          }).catch(() => {
            setSnackbar({ open: true, message: 'Failed to submit feedback', severity: 'error' });
          });
        }}
      />

      {/* Generate Test Cases Modal */}
      {testCaseGenItem && (
        <GenerateTestCasesModal
          open={testCaseGenModalOpen}
          onClose={() => {
            setTestCaseGenModalOpen(false);
            setTestCaseGenItem(null);
          }}
          projectId={selectedProject?.project_id}
          jiraKey={testCaseGenItem?.content?.metadata?.jira_issue_key || 
                   testCaseGenItem?.content?.external_key ||
                   testCaseGenItem?.jira_issue_key || 
                   testCaseGenItem?.external_key}
          storyTitle={testCaseGenItem?.content?.name || testCaseGenItem?.title}
          onTestCasesSaved={handleTestCasesSaved}
        />
      )}

      {/* Bug RCA Modal */}
      <Dialog 
        open={rcaModalOpen} 
        onClose={closeRcaModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '50vh' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="error" />
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="h6" component="span">
                  Root Cause Analysis
                </Typography>
                {rcaData?.is_stale && (
                  <Chip 
                    label="Stale" 
                    size="small" 
                    color="warning" 
                    variant="outlined"
                    sx={{ fontSize: '0.65rem', height: '20px' }}
                    title="This RCA was computed before the bug was last updated. Consider regenerating it."
                  />
                )}
              </Box>
              {rcaIssueKey && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Bug: {rcaIssueKey}
                </Typography>
              )}
              {rcaData && !rcaLoading ? (
                <>
                  {formatRcaAnalyzedCaption(rcaData) ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      {formatRcaAnalyzedCaption(rcaData)}
                    </Typography>
                  ) : null}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {resolveHistoricalScopeNote(rcaData)}
                  </Typography>
                </>
              ) : null}
            </Box>
            {rcaIssueKey && (
              <Chip label={rcaIssueKey} size="small" variant="outlined" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {rcaLoading ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>Loading RCA analysis...</Typography>
            </Box>
          ) : rcaData?.error ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => handleRefreshRca(true)} disabled={rcaGenerating}>
                  {BUG_RCA_COPY.retryAnalysis}
                </Button>
              }
            >
              {rcaData.error}
              {rcaData.unavailable ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {BUG_RCA_COPY.unavailableMessage}
                </Typography>
              ) : null}
            </Alert>
          ) : hasValidRcaPayload(rcaData) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
              {/* Stale Warning */}
              {rcaData.is_stale && (
                <Alert 
                  severity="warning" 
                  icon={<WarningIcon />}
                  action={
                    <Button
                      size="small"
                      color="inherit"
                      onClick={() => handleRefreshRca(true)}
                      disabled={rcaGenerating}
                    >
                      {rcaGenerating ? 'Regenerating...' : 'Regenerate'}
                    </Button>
                  }
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    This RCA may be outdated
                  </Typography>
                  <Typography variant="body2">
                    The bug was updated after this RCA was computed. The analysis may not reflect the current bug content. 
                    Consider regenerating the RCA to get an accurate analysis.
                  </Typography>
                </Alert>
              )}
              
              {/* Summary Banner */}
              {rcaData.has_gaps && (
                <Alert 
                  severity="warning" 
                  icon={<WarningIcon />}
                  sx={{ 
                    '& .MuiAlert-message': {
                      width: '100%'
                    }
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Gaps Detected
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    {rcaData.gap_summary}
                  </Typography>
                </Alert>
              )}
              
              {/* Test Gap Analysis */}
              {rcaData.test_gap_analysis && (
                <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <BugReportIcon color="primary" />
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        {BUG_RCA_COPY.testGapSectionTitle}
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {BUG_RCA_COPY.gapTypePrefix}
                        </Typography>
                        {' '}
                        <Typography component="span" sx={{ color: 'text.secondary' }}>
                          {resolveGapTypeLabel(rcaData.test_gap_analysis)}
                        </Typography>
                        {' '}
                        <Typography component="span" sx={{ color: 'text.secondary' }}>
                          (Confidence: {(rcaData.test_gap_analysis.confidence * 100).toFixed(0)}%)
                        </Typography>
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.primary', lineHeight: 1.6 }}>
                      {rcaData.test_gap_analysis.recommendation}
                    </Typography>
                    {rcaData.test_gap_analysis.similar_tests && rcaData.test_gap_analysis.similar_tests.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block', mb: 1 }}>
                          Similar Test Cases:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {rcaData.test_gap_analysis.similar_tests.slice(0, 3).map((test, idx) => (
                            <Chip 
                              key={idx}
                              label={`${test.title} (${(test.similarity_score * 100).toFixed(0)}%)`}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Requirement Mapping */}
              {rcaData.requirement_mapping && (
                <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <BookIcon color="primary" />
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Requirement Mapping
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Status:
                        </Typography>
                        {' '}
                        <Typography component="span" sx={{ color: 'text.secondary' }}>
                          {rcaData.requirement_mapping.requirement_status}
                        </Typography>
                        {' '}
                        <Typography component="span" sx={{ color: 'text.secondary' }}>
                          (Similarity: {(rcaData.requirement_mapping.similarity_score * 100).toFixed(0)}%)
                        </Typography>
                      </Typography>
                      {rcaData.requirement_mapping.matched_entry_title && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                            Matched Entry:
                          </Typography>
                          {' '}
                          <Typography component="span" sx={{ color: 'text.primary' }}>
                            {rcaData.requirement_mapping.matched_entry_title}
                          </Typography>
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                      {rcaData.requirement_mapping.recommendation}
                    </Typography>
                  </CardContent>
                </Card>
              )}
              
              {/* Impact Analysis Miss */}
              {rcaData.impact_analysis_miss && rcaData.impact_analysis_miss.miss_type !== 'none' && (
                <Card variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <WarningIcon color="warning" />
                      <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600 }}>
                        Impact Analysis Miss
                      </Typography>
                    </Box>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        <Typography component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          Miss Type:
                        </Typography>
                        {' '}
                        <Typography component="span" sx={{ color: 'text.secondary' }}>
                          {rcaData.impact_analysis_miss.miss_type.replace(/_/g, ' ')}
                        </Typography>
                        {' '}
                        <Typography component="span" sx={{ color: 'text.secondary' }}>
                          (Confidence: {(rcaData.impact_analysis_miss.confidence * 100).toFixed(0)}%)
                        </Typography>
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                      {rcaData.impact_analysis_miss.recommendation}
                    </Typography>
                  </CardContent>
                </Card>
              )}
              
              {!rcaData.has_gaps && (
                <Alert severity="success">
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>
                    No gaps detected. This bug appears to be well-covered by existing tests and requirements.
                  </Typography>
                </Alert>
              )}
            </Box>
          ) : (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => handleRefreshRca(true)} disabled={rcaGenerating}>
                  {BUG_RCA_COPY.retryAnalysis}
                </Button>
              }
            >
              {BUG_RCA_COPY.unavailableMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {hasValidRcaPayload(rcaData) && !rcaLoading ? (
            <Button
              onClick={() => handleRefreshRca(true)}
              disabled={rcaGenerating}
            >
              {rcaGenerating ? 'Re-analyzing...' : BUG_RCA_COPY.refreshAnalysis}
            </Button>
          ) : null}
          <Button onClick={closeRcaModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Re-import Loading Modal */}
      <Dialog 
        open={reimportLoadingModal} 
        onClose={closeReimportLoadingModal}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          'data-testid': SV.pk.reimportLoadingDialog,
          sx: {
            borderRadius: 3,
            p: 3
          }
        }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 0 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Re-import in progress
          </Typography>
          <IconButton aria-label="close" onClick={closeReimportLoadingModal} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} alignItems="center" sx={{ py: 2 }}>
            <Box sx={{ textAlign: 'center' }}>
              <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
              <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                {reimportMessage}
              </Typography>
              {reimportDetailLine ? (
                <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
                  {reimportDetailLine}
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  This may take a minute or two...
                </Typography>
              )}
            </Box>
            <Box sx={{ width: '100%' }}>
              <LinearProgress 
                variant="determinate" 
                value={reimportProgress} 
                sx={{ 
                  height: 8, 
                  borderRadius: 4,
                  backgroundColor: 'grey.200',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    backgroundColor: '#667eea'
                  }
                }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                {Math.round(reimportProgress)}%
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Figma Re-import Confirmation Dialog */}
      <Dialog
        open={figmaReimportDialogOpen}
        onClose={closeFigmaReimportDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ 'data-testid': SV.pk.figmaReimportDialog }}
      >
        <DialogTitle component="div" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            {figmaReimportPlan?.prompt_type === 'single_frame_conflict'
              ? 'Figma overlap detected'
              : (figmaReimportPlan?.prompt_type === 'reimport' ? 'Confirm Figma re-import' : 'Confirm Figma action')}
          </Typography>
          <IconButton aria-label="close" onClick={closeFigmaReimportDialog} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Alert severity="info">
              This will refetch the latest design from Figma, overwrite stored knowledge, and move impacted entries to <strong>Staged</strong>.
            </Alert>

            {figmaReimportPlan?.existing_entry && (
              <Alert severity="warning">
                Existing entry matched: <strong>{figmaReimportPlan.existing_entry.title}</strong> ({figmaReimportPlan.existing_entry.status})
              </Alert>
            )}

            {Array.isArray(figmaReimportPlan?.overlaps) && figmaReimportPlan.overlaps.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Also impacts
                </Typography>
                <Stack spacing={1}>
                  {figmaReimportPlan.overlaps.map((o) => (
                    <Box key={o.entry_id} sx={{ p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {o.title} <Typography component="span" variant="caption" color="text.secondary">({o.status})</Typography>
                      </Typography>
                      {Array.isArray(o.overlap_frames) && o.overlap_frames.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          Overlap: {o.overlap_frames.slice(0, 6).map(f => f.frame_name || f.frame_id).join(', ')}
                          {o.overlap_frames.length > 6 ? '…' : ''}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFigmaReimportDialog}>
            Cancel
          </Button>
          {figmaReimportPlan?.prompt_type === 'single_frame_conflict' ? (
            <>
              <Button variant="outlined" data-testid={SV.pk.figmaReimportUpdateExisting} onClick={() => runFigmaReimportExecute('update_existing')}>
                Update existing design(s)
              </Button>
              <Button variant="contained" data-testid={SV.pk.figmaReimportKeepSeparate} onClick={() => runFigmaReimportExecute('keep_separate')}>
                Keep separate (create new)
              </Button>
            </>
          ) : (
            <Button variant="contained" data-testid={SV.pk.figmaReimportProceed} onClick={() => runFigmaReimportExecute('proceed')}>
              Proceed
            </Button>
          )}
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ProductKnowledge;


